import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned actions. On narrow viewports these wrap below the title. */
  actions?: ReactNode;
  /** Rendered above the title — breadcrumbs, or a back link. */
  above?: ReactNode;
  /** Rendered beside the title — a plan chip, a status badge, a mono id. */
  meta?: ReactNode;
  className?: string;
}

/**
 * One page heading, used by every page in both apps.
 *
 * The actions slot is why this exists: "Create key", "Export CSV" and
 * "Suspend" were each positioned by hand before, so the primary action of a
 * page landed in a different place depending on the page.
 */
export function PageHeader({
  title,
  description,
  actions,
  above,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6 flex flex-col gap-3', className)}>
      {above}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            {meta}
          </div>
          {description && (
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/** A section heading inside a page. Below `PageHeader` in the hierarchy. */
export function SectionHeader({
  title,
  description,
  actions,
  className,
}: Omit<PageHeaderProps, 'above' | 'meta'>) {
  return (
    <div className={cn('mb-3 flex items-end justify-between gap-3', className)}>
      <div>
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
